import {
    type FieldValue,
    isBlankValue,
    type ProviderFieldSchema,
    scadaItemCount,
} from '@/domain/providerConfig';
import { toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import { normalizeValue } from '@/routes/shared/ProviderConfig/form/formLeaves';
import type { FormFieldValue } from '@/routes/shared/ProviderConfig/form/formValues';

const SCADA_ITEMS_FORM_KEY = toFormKey('extra_config.scada_items');

export interface PenSignalState {
    /** The pen's form values, by form key. */
    formValues: Readonly<Record<string, FormFieldValue>> | undefined;
    /** What the pen's row stores, by schema key; null for a pen the save creates. */
    stored: Readonly<Record<string, FieldValue>> | null;
    /** Signals the user asked for through "Map another signal". */
    added: ReadonlySet<string>;
    /** Whether an error, a conflict or an unreadable value sits at the field. */
    needsAttention: (field: ProviderFieldSchema) => boolean;
}

function formValueOf(state: PenSignalState, field: ProviderFieldSchema): FieldValue {
    return normalizeValue(state.formValues?.[toFormKey(field.key)]);
}

/**
 * S1: a Clarify pen with SCADA items and no daily signal mapped is not fetched daily, so no
 * signal is required of it, as the save rules say.
 */
export function isScadaOnlyPen(
    signalFields: readonly ProviderFieldSchema[],
    state: Pick<PenSignalState, 'formValues'>
): boolean {
    const values = state.formValues;
    if (scadaItemCount(normalizeValue(values?.[SCADA_ITEMS_FORM_KEY])) === 0) return false;
    return signalFields.every((field) =>
        isBlankValue(normalizeValue(values?.[toFormKey(field.key)]))
    );
}

/**
 * The signals a pen shows: the required ones, every one it maps or stores, any the user
 * added, and any with something to say. The rest wait behind "Map another signal", so a
 * pen is not a wall of empty optional boxes, and nothing stored is ever out of sight.
 */
export function splitSignals(
    signalFields: readonly ProviderFieldSchema[],
    state: PenSignalState
): { shown: ProviderFieldSchema[]; hidden: ProviderFieldSchema[] } {
    const isShown = (field: ProviderFieldSchema): boolean =>
        field.required ||
        state.added.has(field.key) ||
        !isBlankValue(formValueOf(state, field)) ||
        !isBlankValue(state.stored?.[field.key] ?? null) ||
        state.needsAttention(field);
    return {
        shown: signalFields.filter(isShown),
        hidden: signalFields.filter((field) => !isShown(field)),
    };
}
